/**
 * Coach profile (client view) – full bio, intro video, Request Coaching.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { getCoach } from '../../domain/usecases/coaching';
import { requestCoaching } from '../../domain/usecases/coaching';
import Card from '../../shared/ui/Card.jsx';
import Avatar from '../../shared/ui/Avatar.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Button from '../../shared/ui/Button.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import Display from '../../shared/ui/Display.jsx';
import Body from '../../shared/ui/Body.jsx';
import Stat from '../../shared/ui/Stat.jsx';
import Textarea from '../../shared/ui/Textarea.jsx';
import styles from './CoachProfilePage.module.css';

export function CoachProfilePage() {
  const { coachId } = useParams();
  const { userId } = useAuth();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');

  useEffect(() => {
    if (!coachId) return;
    setLoading(true);
    getCoach(coachId)
      .then(setCoach)
      .finally(() => setLoading(false));
  }, [coachId]);

  async function handleRequest() {
    if (!userId || !coachId) return;
    setError('');
    setSending(true);
    try {
      await requestCoaching(userId, coachId, requestMessage.trim());
      setSent(true);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton width="100%" height={300} radius="var(--radius-md)" />
      </div>
    );
  }

  if (!coach) {
    return (
      <div className={styles.page}>
        <Body tone="secondary">Coach not found.</Body>
      </div>
    );
  }

  if (coach.approvalStatus !== 'approved' || !coach.isActiveCoach) {
    return (
      <div className={styles.page}>
        <Body tone="secondary">This coach is not available.</Body>
      </div>
    );
  }

  const hasStats = coach.yearsExperience > 0;

  return (
    <div className={styles.page}>
      <Link to="/coaching" className={styles.back}>← Back to coaches</Link>

      <div className={styles.header}>
        <Avatar
          src={coach.profileImage}
          initials={coach.name.charAt(0)}
          size="lg"
          verified={coach.isVerified}
          alt={coach.name}
        />
        <Display as="h1" size="l" style={{ marginTop: '0.75rem', marginBottom: '0.25rem' }}>
          {coach.name}
        </Display>
        {coach.specialization && (
          <Badge variant="gold" style={{ textTransform: 'capitalize' }}>
            {coach.specialization}
          </Badge>
        )}
      </div>

      {hasStats && (
        <div className={styles.stats}>
          {coach.yearsExperience > 0 && (
            <Stat label="Experience" value={`${coach.yearsExperience}y`} />
          )}
        </div>
      )}

      {coach.introVideoUrl && (
        <div className={styles.videoWrap}>
          <video src={coach.introVideoUrl} controls className={styles.video} />
        </div>
      )}

      {coach.bio && (
        <Card className={styles.bioCard}>
          <Body tone="secondary" as="p" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
            {coach.bio}
          </Body>
        </Card>
      )}

      <section className={styles.section}>
        {!userId ? (
          <Body tone="secondary">Log in to request coaching from this coach.</Body>
        ) : userId === coachId ? (
          <Card padding="md">
            <Body tone="secondary">This is your coach profile. Manage it from the coach dashboard.</Body>
            <Button variant="ghost" as={Link} to="/coaching" size="sm">Browse other coaches</Button>
          </Card>
        ) : sent ? (
          <Card frame="gold">
            <Body tone="primary">Request sent. The coach will respond soon.</Body>
          </Card>
        ) : (
          <>
            <Textarea
              label="MESSAGE TO COACH (OPTIONAL)"
              placeholder="Goals, equipment, injuries, availability"
              value={requestMessage}
              onChange={(e) => setRequestMessage(e.target.value)}
              rows={3}
            />
            <Button
              variant="primary"
              size="lg"
              disabled={sending}
              onClick={handleRequest}
              style={{ width: '100%', marginTop: '0.75rem' }}
            >
              {sending ? 'Sending…' : 'Request coaching'}
            </Button>
            {error && (
              <Body as="p" style={{ color: 'var(--red-platform)', fontSize: 14, marginTop: '0.5rem' }}>
                {error}
              </Body>
            )}
          </>
        )}
      </section>
    </div>
  );
}
